import { useCallback, useEffect, useMemo, useState } from "react";
import TableHeader from "../Components/TableHeader";
import "../Stylesheets/Users/Users.css";
import core from "../core";
import { CheckCircleFill, PersonAdd, ThreeDotsVertical, XCircleFill } from "react-bootstrap-icons";
import TableFoot from "../Components/TableFoot";
import TableLoader from "../Components/TableLoader";
import AddNewUser from "../Components/AddNewUser";
import OutsideClickHandler from "react-outside-click-handler";
import DialogModal from "../Components/DialogModal";
import ChangePasswordModal from "../Components/ChangePasswordModal";
import { Modal } from "react-bootstrap";
import api, { isCanceled } from "../lib/api";
import useNotify from "../hooks/useNotify";
import useListData from "../hooks/useListData";
import useTableFilter from "../hooks/useTableFilter";

const EMPTY_FORM = {
    username: "", password: "", repeat_password: "", email: "", copart_accounts: [], iaai_accounts: [],
    copart_status: false, iaai_status: false, limited_amount: "0", purchase_limit: "0",
    is_active: true, name: "", surname: "", phone: "", role: "", admin: ""
};

const CHECKBOXES = [
    { name: "eligible_to_bid", value: "Eligible To Bid" },
    { name: "inactive_users", value: "Inactive Users" },
    { name: "blocked", value: "Blocked" },
    { name: "active", value: "Is Active" },
];

// Buyer lists arrive either as a JSON string or an already-parsed array.
const toAccountList = (value) => {
    if (value == null || value === "null") return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const Users = ({ setNotification }) => {
    const notify = useNotify(setNotification);
    const { filter, setFilter, clampPage } = useTableFilter({
        page: 1, limit: 10, keyword: "", sort_by: "", sort_order: "DESC",
        eligible_to_bid: "", blocked: "", admin_id: "", active: "", inactive_users: "",
    });

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [sortBy, setSortBy] = useState([]);
    const [modals, setModals] = useState({ createUser: false, deleteUser: false });
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [active, setActive] = useState("");
    const [action, setAction] = useState("add");
    const [dialog, setDialog] = useState({ show: false, title: "", text: "", id: "", loader: false });
    const [changePassword, setChangePassword] = useState({ show: false, id: "", password: "", repeat_password: "", loader: false });
    const [exportLoad, setExportLoad] = useState(false);
    const [exportLimit, setExportLimit] = useState({ modal: false, limit: "10" });
    const [changeBuyersForm, setChangeBuyersForm] = useState({ current_buyer: "", new_buyer: "", account: "copart", show: false });
    const [load, setLoad] = useState(false);

    const onListError = useCallback((err) => notify.error(err), [notify]);
    const { data: users, total, loading, reload } = useListData("/users", filter, { onError: onListError });

    const pageCount = useMemo(
        () => Math.max(1, Math.ceil(total / filter.limit)),
        [total, filter.limit]
    );

    // Keeps the user on a page that still exists after rows are removed.
    useEffect(() => { clampPage(total); }, [total, clampPage]);

    useEffect(() => {
        const controller = new AbortController();

        api.get('/options', { params: { name: "user-sort-by" }, signal: controller.signal })
            .then((res) => {
                const data = res.data.data;
                const parsed = typeof data === "string" ? JSON.parse(data) : data;
                setSortBy(Array.isArray(parsed) ? parsed : []);
            })
            .catch((err) => {
                if (isCanceled(err)) return;
                setSortBy([]);
                notify.error(err, "Could not load sort options.");
            });

        return () => controller.abort();
    }, [notify]);

    const handlers = {
        openEditUserModal(user) {
            setActive("");
            setFormData({
                ...EMPTY_FORM,
                username: user.username,
                email: user.email,
                copart_accounts: toAccountList(user.copart_accounts).map((acc) => ({ label: acc, value: acc })),
                iaai_accounts: toAccountList(user.iaai_accounts).map((acc) => ({ label: acc, value: acc })),
                copart_status: user.copart_status,
                iaai_status: user.iaai_status,
                limited_amount: user.limited_amount,
                purchase_limit: user.purchase_limit,
                is_active: user.is_active,
                name: user.name, surname: user.surname, phone: user.phone,
                role: user.role, admin: user.admin_id, id: user.id,
            });
            setAction("edit");
            // Opened directly instead of behind an uncleared 500ms timeout that
            // fired after navigation and set state on an unmounted component.
            setModals((prev) => ({ ...prev, createUser: true }));
        },
        addNewHandler() {
            setFormData(EMPTY_FORM);
            setAction("add");
        },
        openRemoveUserModal(user) {
            setDialog({ show: true, title: "Remove User", text: "Do you want to remove this user?", id: user.id, loader: false });
            setActive("");
        },
        deleteUser() {
            setDialog((prev) => ({ ...prev, loader: true }));
            api.delete(`/users/${dialog.id}`)
                .then((res) => {
                    notify.success(res.data.message.en);
                    setSelectedUsers((prev) => prev.filter((id) => id !== dialog.id));
                    reload();
                })
                .catch((err) => notify.error(err))
                .finally(() => setDialog({ show: false, title: "", text: "", id: "", loader: false }));
        },
        openChangePasswordModal(user) {
            setActive("");
            setChangePassword({ show: true, id: user.id, password: "", repeat_password: "", loader: false });
        },
        changePasswordHandler(e) {
            e.preventDefault();
            if (changePassword.password !== changePassword.repeat_password) {
                notify.error(null, "Passwords don't match");
                return;
            }
            if (changePassword.password.length < 6) {
                notify.error(null, "Password must be at least 6 characters");
                return;
            }
            setChangePassword((prev) => ({ ...prev, loader: true }));
            api.put(`/users/${changePassword.id}/change-password`, { password: changePassword.password })
                .then((res) => {
                    notify.success(res.data.message.en);
                    setChangePassword({ show: false, id: "", password: "", repeat_password: "", loader: false });
                })
                .catch((err) => {
                    notify.error(err);
                    setChangePassword((prev) => ({ ...prev, loader: false }));
                });
        },
        exportUsers() {
            setExportLoad(true);
            api.get('/export/users', { params: { ...filter, limit: exportLimit.limit } })
                .then((res) => {
                    // Opened as a root-relative path. Prefixing it with
                    // window.location.hostname (which has no scheme) made the
                    // browser treat it as a relative path and 404.
                    window.open(res.data.url, "_blank", "noopener");
                    setExportLimit((prev) => ({ ...prev, modal: false }));
                })
                .catch((err) => notify.error(err, "Export failed."))
                .finally(() => setExportLoad(false));
        },
        changeBuyers(e) {
            e.preventDefault();
            if (selectedUsers.length === 0) {
                notify.error(null, "Select at least one user first.");
                return;
            }
            setLoad(true);
            api.put('/change-buyer', {
                current_buyer: changeBuyersForm.current_buyer,
                new_buyer: changeBuyersForm.new_buyer,
                account: changeBuyersForm.account,
                selectedUsers,
                bySelected: true,
            })
                .then((res) => {
                    notify.success(res.data.message.en);
                    setChangeBuyersForm({ current_buyer: "", new_buyer: "", account: "copart", show: false });
                    // Cleared, so a second submit can't silently reuse the
                    // previous selection.
                    setSelectedUsers([]);
                    reload();
                })
                .catch((err) => notify.error(err))
                .finally(() => setLoad(false));
        },
        toggleSelectAll(checked) {
            // The header checkbox had an empty if/else and did nothing.
            setSelectedUsers(checked ? users.map((user) => user.id) : []);
        },
    };

    const allSelected = users.length > 0 && users.every((user) => selectedUsers.includes(user.id));

    return (
        <div className="page-content-container">
            <h2 className="page-title">Users</h2>
            <TableHeader
                addNewHandler={handlers.addNewHandler}
                exportHandler={() => setExportLimit((prev) => ({ ...prev, modal: true }))}
                exportLoad={exportLoad}
                setNotification={setNotification}
                AddNewButtonIcon={PersonAdd}
                setModals={setModals}
                filter={filter}
                sortBy={sortBy}
                exportButton={true}
                checkboxes={CHECKBOXES}
                setFilter={setFilter}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                    className="export-button"
                    style={{ marginTop: "10px" }}
                    onClick={() => setChangeBuyersForm({ current_buyer: "", new_buyer: "", account: "copart", show: true })}
                >
                    Change Buyers{selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ""}
                </button>
            </div>
            <div className="table-scroll">
                <table className="responsive-d-table">
                    <thead>
                        <tr>
                            <th style={{ width: "33px" }}>
                                <input
                                    style={{ cursor: "pointer" }}
                                    type="checkbox"
                                    aria-label="Select all users on this page"
                                    checked={allSelected}
                                    onChange={(e) => handlers.toggleSelectAll(e.target.checked)}
                                />
                            </th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Copart</th>
                            <th>Copart Accounts</th>
                            <th>IAAI</th>
                            <th>IAAI Accounts</th>
                            <th>Last Login</th>
                            <th>Last Bid</th>
                            <th>Eligible To Bid</th>
                            <th><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!loading && users.length > 0) && users.map((user, id) => {
                            const copart = toAccountList(user.copart_accounts).join(", ");
                            const iaai = toAccountList(user.iaai_accounts).join(", ");
                            return <tr key={user.id} className={(id + 2) % 2 === 0 ? "even-row" : "odd-row"}>
                                <td>
                                    {/* Controlled, so the tick clears when the
                                        selection is reset after a submit. */}
                                    <input
                                        type="checkbox"
                                        aria-label={`Select ${user.username}`}
                                        checked={selectedUsers.includes(user.id)}
                                        onChange={() => setSelectedUsers((prev) => (
                                            prev.includes(user.id)
                                                ? prev.filter((id) => id !== user.id)
                                                : [...prev, user.id]
                                        ))}
                                    />
                                </td>
                                <td>{user.username}</td>
                                <td>{user.email}</td>
                                <td>{user.copart_status ? <CheckCircleFill className="status-yes" /> : <XCircleFill className="status-no" />}</td>
                                <td title={copart}>{copart.slice(0, 20)}</td>
                                <td>{user.iaai_status ? <CheckCircleFill className="status-yes" /> : <XCircleFill className="status-no" />}</td>
                                <td title={iaai}>{iaai.slice(0, 20)}</td>
                                <td>{user.last_login_date ? core.formdatDate(new Date(user.last_login_date), 'yyyy-mm-dd hr:min') : "Not Defined"}</td>
                                <td>{user.last_bid_date ? core.formdatDate(new Date(user.last_bid_date), 'yyyy-mm-dd hr:min') : "Not Defined"}</td>
                                <td>{user.eligible_to_bid ? <CheckCircleFill className="status-yes" /> : <XCircleFill className="status-no" />}</td>
                                <td>
                                    <button
                                        type="button"
                                        className="row-actions-trigger"
                                        aria-label={`Actions for ${user.username}`}
                                        onClick={() => setActive((prev) => (prev === user.id ? "" : user.id))}
                                    >
                                        <ThreeDotsVertical />
                                    </button>
                                    {active === user.id && <OutsideClickHandler onOutsideClick={() => setActive("")}>
                                        <div className="table-actions-box">
                                            <ul>
                                                <li onClick={() => handlers.openEditUserModal(user)}>Edit</li>
                                                <li onClick={() => handlers.openChangePasswordModal(user)}>Change password</li>
                                                <li onClick={() => handlers.openRemoveUserModal(user)}>Delete</li>
                                            </ul>
                                        </div>
                                    </OutsideClickHandler>}
                                </td>
                            </tr>
                        })}
                        {(!loading && users.length === 0) && <tr className="empty-row">
                            <td colSpan={11}><div className="empty-records"><span>Records not found</span></div></td>
                        </tr>}
                        {loading && <TableLoader colspan={11} />}
                    </tbody>
                </table>
            </div>
            <TableFoot filter={filter} setFilter={setFilter} count={pageCount} total={total} />
            <AddNewUser
                onSaved={reload}
                setNotification={setNotification}
                action={action}
                show={modals.createUser}
                setShow={(value) => setModals((prev) => ({ ...prev, createUser: value }))}
                formData={formData}
                setFormData={setFormData}
            />
            <DialogModal
                show={dialog.show}
                setShow={(value) => setDialog((prev) => ({ ...prev, show: value }))}
                title={dialog.title}
                text={dialog.text}
                onSubmit={handlers.deleteUser}
                loader={dialog.loader}
            />
            <ChangePasswordModal changePassword={changePassword} setChangePassword={setChangePassword} onSubmit={handlers.changePasswordHandler} />
            <Modal show={exportLimit.modal} onHide={() => setExportLimit((prev) => ({ ...prev, modal: false }))} centered>
                <Modal.Header closeButton><h5 className="modal-heading">Export Data Limit</h5></Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <label htmlFor="export-limit">Select Limit</label>
                        <select
                            id="export-limit"
                            value={exportLimit.limit}
                            onChange={(e) => setExportLimit((prev) => ({ ...prev, limit: e.target.value }))}
                            className="form-control"
                        >
                            <option value={5000}>All</option>
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </div>
                    <button disabled={exportLoad} style={{ marginLeft: "0", marginTop: "20px" }} onClick={handlers.exportUsers} className="export-button">
                        {exportLoad ? <div className="button-loader"></div> : "Export"}
                    </button>
                </Modal.Body>
            </Modal>
            <Modal show={changeBuyersForm.show} onHide={() => setChangeBuyersForm((prev) => ({ ...prev, show: false }))} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="modal-heading">Change Buyer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="change-buyers-form" onSubmit={handlers.changeBuyers}>
                        <div className="form-group mt-3">
                            <label htmlFor="current-buyer">Current Buyer</label>
                            <input id="current-buyer" required className="form-control" value={changeBuyersForm.current_buyer} onChange={(e) => setChangeBuyersForm((prev) => ({ ...prev, current_buyer: e.target.value }))} placeholder="Current Buyer" />
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="new-buyer">New Buyer</label>
                            <input id="new-buyer" required className="form-control" value={changeBuyersForm.new_buyer} onChange={(e) => setChangeBuyersForm((prev) => ({ ...prev, new_buyer: e.target.value }))} placeholder="New Buyer" />
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="buyer-account">Account</label>
                            <select id="buyer-account" className="form-control" value={changeBuyersForm.account} onChange={(e) => setChangeBuyersForm((prev) => ({ ...prev, account: e.target.value }))}>
                                <option value="copart">Copart</option>
                                <option value="iaai">IAAI</option>
                            </select>
                        </div>
                        <p className="form-hint">
                            {selectedUsers.length === 0
                                ? "Select users in the table first."
                                : `Applies to ${selectedUsers.length} selected user${selectedUsers.length === 1 ? "" : "s"}.`}
                        </p>
                        {/* The submit button previously also carried an onClick
                            whose body was only a comment. */}
                        <button disabled={load || selectedUsers.length === 0} type="submit" className="submit-modal-form-btn" style={{ width: "100%" }}>
                            {load ? "Changing..." : "Change Buyer"}
                        </button>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default Users
